import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import GatewayService from "../services/GatewayService";
import {
  GrantRoleAssignmentsInput,
  RevokeRoleAssignmentsInput,
} from "../components/supportFunctions/graphql/PersonQueries";
import UsersService from "@velocity-ehs/user-utils-module";
import { AvailableRole } from "./useGetAvailableRoles";

export enum PersonRoleActions {
  ASSIGN = "assign",
  REVOKE = "revoke",
}

export interface AssignRolesInput {
  roles: AvailableRole[];
  locations: string[];
}

export interface RevokeRolesInput {
  id: string;
  location: any;
  role: any;
}

const usePersonRolesMutation = (userId: string) => {
  const assign = useMutation(
    (req: GrantRoleAssignmentsInput[]) => GatewayService.assignRoles(req),
    ResponseHandling("person_role_assign_success")
  );
  const revoke = useMutation(
    (req: RevokeRoleAssignmentsInput[]) => GatewayService.revokeRoles(req),
    ResponseHandling("person_role_revoke_success")
  );

  const mutate = async (
    action: PersonRoleActions,
    input: AssignRolesInput | RevokeRolesInput[]
  ) => {
    switch (action) {
      case PersonRoleActions.ASSIGN:
        const assignRoles = mapFormToAssign(userId, input as AssignRolesInput);
        return await assign.mutateAsync(assignRoles);
      case PersonRoleActions.REVOKE:
        const revokeRoles = mapFormToRevoke(
          userId,
          input as RevokeRolesInput[]
        );
        return await revoke.mutateAsync(revokeRoles);
      default:
        throw new Error(`Invalid action: ${action}`);
    }
  };

  return {
    mutations: {
      mutate,
      isLoading: assign.isLoading || revoke.isLoading,
    },
  };
};

export default usePersonRolesMutation;

function mapFormToAssign(
  userId: string,
  inputs: AssignRolesInput
): GrantRoleAssignmentsInput[] {
  return inputs.roles.map((role) => {
    return {
      customerId: UsersService.customer.id,
      locationIds: inputs.locations,
      roleId: role.id,
      userId,
    };
  });
}

function mapFormToRevoke(
  userId: string,
  inputs: RevokeRolesInput[]
): RevokeRoleAssignmentsInput[] {
  return inputs.map((assignment) => {
    return {
      customerId: UsersService.customer.id,
      locationIds: [assignment.location.id],
      roleId: assignment.role.id,
      userId,
    };
  });
}

function ResponseHandling(messageKey: string) {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    onSuccess: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
    },
  };
}
